/* eslint-disable no-extend-native */
import React, { createContext, ReactNode, useContext, useEffect } from 'react';
import { useAuthContext } from 'contexts/auth';

const TimezoneContext = createContext<string>(
  Intl.DateTimeFormat().resolvedOptions().timeZone
);

export function TimezoneProvider({ children }: { children: ReactNode }) {
  const { timezone: companyTZ } = useAuthContext();

  useEffect(() => {
    const OrigDTF = Intl.DateTimeFormat;
    const origToLocaleString = Date.prototype.toLocaleString;
    const origToLocaleDateString = Date.prototype.toLocaleDateString;
    const origToLocaleTimeString = Date.prototype.toLocaleTimeString;

    // Wrapper function to support both new and call invocations
    function CompanyDTF(
      locales?: string | string[],
      opts: Intl.DateTimeFormatOptions = {}
    ) {
      return new OrigDTF(locales, { ...opts, timeZone: companyTZ });
    }
    // Preserve prototype and static methods
    CompanyDTF.prototype = OrigDTF.prototype;
    CompanyDTF.supportedLocalesOf = OrigDTF.supportedLocalesOf.bind(OrigDTF);

    // Override global DateTimeFormat
    // @ts-ignore
    Intl.DateTimeFormat = CompanyDTF;

    // Patch Date.prototype methods to use our overridden Intl
    Date.prototype.toLocaleString = function (
      locale?: string,
      opts?: Intl.DateTimeFormatOptions
    ) {
      return new Intl.DateTimeFormat(locale, opts).format(this);
    };
    Date.prototype.toLocaleDateString = function (
      locale?: string,
      opts?: Intl.DateTimeFormatOptions
    ) {
      return new Intl.DateTimeFormat(locale, opts).format(this);
    };
    Date.prototype.toLocaleTimeString = function (
      locale?: string,
      opts?: Intl.DateTimeFormatOptions
    ) {
      return new Intl.DateTimeFormat(locale, opts).format(this);
    };

    return () => {
      // Restore originals
      // @ts-ignore
      Intl.DateTimeFormat = OrigDTF;
      Date.prototype.toLocaleString = origToLocaleString;
      Date.prototype.toLocaleDateString = origToLocaleDateString;
      Date.prototype.toLocaleTimeString = origToLocaleTimeString;
    };
  }, [companyTZ]);

  return (
    <TimezoneContext.Provider value={companyTZ}>
      {children}
    </TimezoneContext.Provider>
  );
}

export const useTimezone = () => useContext(TimezoneContext);
